(() => {
  const ROOT_DOMAIN = window.Shopify.routes.root;
  let cart = null;

  /*
    * Fetch the cart and update the local cart object
    * @return {obj} the cart
  */
  const getCart = () => new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(ROOT_DOMAIN + 'cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'pragma': 'no-cache',
          'cache-control': 'no-cache'
        }
      });

      if (!response?.ok && response.status === 422) {
        const data = await response.json();
        throw Error('Can\'t get cart data', {
          cause: {
            status: response.status,
            message: data.message || data.description || response.statusText
          }
        }, 'cart-manager.js');
      } else if (!response?.ok) {
        throw 'Something went wrong.';
      }

      const data = await response.json();

      await dispatchUpdateLocalCart(data);

      resolve(data);
    } catch (error) {
      reject(error);
    }
  });

  /*
    * Return the local cart
    * @return {obj} the local cart
  */
  const getLocalCart = () => cart;

  /*
    * Return the local cart when it's ready
    * @return {obj} the cart
  */
  const getLocalCartAsync = () => new Promise(resolve => {
    if (cart) {
      resolve(cart);
    } else {
      let count = 0;
      const waitForCartManagerAPI = () => {
        setTimeout(async () => {
          if (cart) {
            resolve(cart);
          } else if (count < 15) {
            count++;
            waitForCartManagerAPI();
          } else {
            console.error('CartManager API is not available');
            resolve(null);
          }
        }, count * 100);
      };
      waitForCartManagerAPI();
    }
  });

  /*
    * Update the local cart
    * @return {obj} the local cart
  */
  const updateLocalCart = (data) => {
    cart = data;
    return cart;
  }

  /*
    * Dispatch update for local cart
    * @return {boolean} true if the cart was updated
  */
  const dispatchUpdateLocalCart = (data) => new Promise((resolve, reject) => {
    try {
      delete data.token;
      // delete data.attributes;
      delete data.note;
      delete data.total_weight;
      data.items.forEach(item => {
        for (const key in item.properties) {
          try {
            item.properties[key] = JSON.parse(item.properties[key]);
          } catch (err) {
          }
        }
      });
      data.order_discount_codes = data.cart_level_discount_applications.reduce((acc, curr) => {
        const currTitle = curr.title?.toUpperCase() || curr.discount_application?.title?.toUpperCase();
        if (acc.some(item => {
          const itemTitle = item.title.toUpperCase();
          return itemTitle === currTitle;
        })) {
          acc.forEach(item => {
            const itemTitle = item.title.toUpperCase();
            return itemTitle === currTitle ? item.amount += curr.amount ?? curr.total_allocated_amount ?? 0 : null;
          });
        } else {
          acc.push({
            title: currTitle || curr.discount_application?.title,
            amount: curr.amount ?? curr.total_allocated_amount ?? 0
          })
        }
        return acc;
      }, []);
      data.total_order_discount_codes = data.order_discount_codes.reduce((acc, curr) => acc + curr.amount, 0);
      data.product_discount_codes = data.items
        .reduce((acc, curr) =>
          acc.concat(
            curr.discounts.map(discount => ({ ...discount, title: discount.title.toUpperCase() })).reduce((acc, discount) => {
              if (!['FREE GIFT', 'PRODUCT DISCOUNT', 'BUNDLE DISCOUNT', 'TIERED DISCOUNT'].includes(discount.title)) {
                if (acc.every(item => item.title.toLowerCase() != discount.title.toLowerCase())) acc.push(discount);
              }
              return acc;
            }, [])
          )
        , [])
        .filter(item => item.amount)
        .reduce((acc, curr) => {
          const currTitle = curr.title.toUpperCase();
          // Prefer order-level when the same title exists in both buckets
          if (data.order_discount_codes.some(item => item.title.toUpperCase() === currTitle)) return acc;
          const item = acc.find(item => item.title.toUpperCase() === currTitle);
          if (item) {
            item.amount += curr.amount;
          } else {
            acc.push({ ...curr, title: currTitle });
          }
          return acc;
        }, []);
      data.total_product_discount_codes = data.product_discount_codes.reduce((acc, curr) => acc + curr.amount, 0);
      data.promo_discount = data.items
        .reduce((acc, curr) =>
          acc.concat(
            curr.discounts.map(discount => ({ ...discount, title: discount.title.toUpperCase() })).reduce((acc, discount) => {
              if (['FREE GIFT', 'PRODUCT DISCOUNT', 'BUNDLE DISCOUNT', 'TIERED DISCOUNT'].includes(discount.title)) {
                if (acc.every(item => item.title.toLowerCase() != discount.title.toLowerCase())) acc.push(discount);
              }
              return acc;
            }, [])
          )
        , [])
        .filter(item => item.amount)
        .reduce((acc, curr) => {
          const item = acc.find(item => item.title === curr.title);
          if (item) {
            const itemIndex = acc.findIndex(item => item.title === curr.title);
            acc[itemIndex] = {title: item.title, amount: item.amount + curr.amount};
          } else {
            acc.push(curr);
          }
          return acc;
        }, []);
      data.total_promo_discount = data.promo_discount.reduce((acc, curr) => acc + curr.amount, 0);
      data.total_product_discount = data.total_discount - data.total_order_discount_codes;
      data.discount_codes = [...data.product_discount_codes, ...data.order_discount_codes]
        .reduce((acc, curr) => {
          const currTitle = (curr.title || '').toUpperCase();
          if (!currTitle || acc.some(item => item.title.toUpperCase() === currTitle)) return acc;
          acc.push({ ...curr, title: currTitle });
          return acc;
        }, [])
        .sort((a, b) => a.title.localeCompare(b.title));
      data.total_without_discount_codes = data.original_total_price - data.total_promo_discount;
      data.total_with_discount_codes = data.original_total_price - data.total_discount;
      if (window.cart_settings?.cartDisplayDiscountCodes === '1') {
        data.discount = data.total_promo_discount;
        data.total = data.total_with_discount_codes;
      } else if (window.cart_settings?.cartDisplayDiscountCodes === '2') {
        data.discount = data.total_discount;
        data.total = data.total_with_discount_codes;
      } else {
        data.discount = data.total_promo_discount;
        data.total = data.total_without_discount_codes;
      }
      data.shipping_price = data.total_price - data.total;
      updateLocalCart(data);
      window.broadcastChannel.updateLocalCart(data);
      resolve(true);
    } catch (error) {
      reject(error);
    }
  });

  /*
    * sanitize discounts array for SGD
    * @return the sanitized array
  */
  const sanitizeArray = array => array ? array.map((item, index, arr) => {
      if (!item || isNaN(item) || item < 0) return 0;
      if (index > 1 && !arr[index - 1]) return 0;
      if (index > 1 && item <= arr[index - 1]) return 0;
      return item;
    }).filter(item => item) : null;

  /*
    * Clear the entire cart
    * @return {obj} The new empty cart
  */
  const clearCart = () => new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(ROOT_DOMAIN + 'cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response?.ok && response.status === 422) {
        const data = await response.json();
        throw Error('Can\'t clear cart', {
          cause: {
            status: response.status,
            message: data.message || data.description || response.statusText
          }
        }, 'cart-manager.js');
      } else if (!response?.ok) {
        throw 'Something went wrong.';
      }

      const data = await response.json();

      await dispatchUpdateLocalCart(data);

      clearLocalCart();

      resolve(data);
    } catch (error) {
      reject(error);
    }
  });

  /*
    * Clear the local cart
  */
  const clearLocalCart = () => {
    cart = null;
  }

  /*
    * Remove bundles and gifts from cart when the features are off
    * @param {obj} settings | Cart settings
    * @return {boolean} true if the cart was sanitized, false if not
  */
  const sanitize = (settings) => new Promise(async (resolve, reject) => {
    try {
      let sanitized = false;

      const sanitizeCart = async (cart) => {
        let item = null;

        // Remove GWP when the feature is off
        const items = cart.items.filter(item => item.properties._isGWP);
        if (!settings.gwpEnabled && items.length) {
          const data = await removeItem(items[0].key);
          sanitized = true;
          await sanitizeCart(data);
          return;
        } else if (settings.gwpEnabled && items.length > 1 && settings.gwpOnlyOneGiftEnabled) {
          let item = items.find(item => item.handle === settings.gwpThirdGift);
          if (!item) item = items.find(item => item.handle === settings.gwpSecondGift);
          if (!item) item = items.find(item => item.handle === settings.gwpFirstGift);
          if (item) {
            const data = await removeItem(item.key);
            sanitized = true;
            await sanitizeCart(data);
            return;
          }
        }

        // Remove bundles created with the Bundle Button from cart if bundle button feature is off or if the bundle button tag is not present in the item anymore
        // Tag used on Bundle functionality from theme => "bundle-button-"
        item = cart.items.find(item => {
          if (!settings.bbPdpEnabled && item.properties._bbId) {
            return true;
          } else if (item.properties._bbId && item.properties._tags?.every(tag => !tag.includes('bundle-button-'))) {
            return true;
          }
          return false;
        });

        // Remove bundles created with the Bundle Button from cart if bundle button feature is off or if the bundle button tag is not present in the item anymore
        // Tag used on Bundle Discoutount functionality from Discount Kit App => "bundle-discount-button-" or "bundle-discount-button-ca-"
        if(window.cart_settings.dkBBDiscountTag) {
          item = cart.items.find(item => {
            if (!settings.dkBBPdpEnabled && item.properties._bbId) {
              return true;
            } else if (item.properties._bbId && item.properties._tags?.every(tag => !tag.includes(window.cart_settings.dkBBDiscountTag))) {
              return true;
            }
            return false;
          });
        }

        if (item) {
          const data = await removeItem(item.key);
          sanitized = true;
          await sanitizeCart(data);
          return;
        }

        // Remove auto bundles (AB) if the feature is off
        item = cart.items.find(item => {
          if (!settings.abEnabled && item.properties._abTag) {
            return true;
          } else if (item.properties._abTag && item.properties._tags?.every(tag => !tag.includes('auto-bundle-'))) {
            return true;
          }
          return false;
        });
        if (item) {
          const data = await removeBundle(item.properties._bundleId);
          sanitized = true;
          await sanitizeCart(data);
          return;
        }
      }

      await sanitizeCart(cart);

      if (sanitized) {
        resolve({ status: 'updated', ok: true, cart, error: null });
      } else {
        resolve({ status: 'not updated', ok: true, cart, error: null });
      }
    } catch (error) {
      reject({ status: 'fail', ok: false, cart, error });
    }
  });

  /*
    * Get the attributes object to update the cart
    * @return {obj} The attributes object
  */
  const getCartAttributes = (settings) => {
    const attributes = {
      __fsEnabled: null,
      __gwpEnabled: null,
      __bbPdpEnabled: null,
      __dkBBPdpEnabled: null,
      __sgdThresholds: null,
      __sgdDiscountType: null,
      __sgdDiscounts: null,
      __sgdThresholdApplyDiscountCode: null,
      __sgdExcludeThresholdTagName: null,
      __sgdExcludeDiscountTagName: null,
      __bdDiscountType: null,
      __bdFirstDiscountEnabled: null,
      __bdDiscounts: null,
      __bdTag: null,
      __loyalty: settings.loyalty || null
    };

    // Free Shipping
    if (settings.fsEnabled || settings.fsEnabledCa) {
      attributes.__fsEnabled = {
        us: settings.fsEnabled || false,
        ca: settings.fsEnabledCa || false
      }
    }

    // GWP
    if (settings.gwpEnabled || settings.gwpEnabledCa) {
      attributes.__gwpEnabled = {
        us: settings.gwpEnabled || false,
        ca: settings.gwpEnabledCa || false
      }
    }

    // BB
    if (settings.bbPdpEnabled || settings.bbPdpEnabledCa) {
      attributes.__bbPdpEnabled = {
        us: settings.bbPdpEnabled || false,
        ca: settings.bbPdpEnabledCa || false
      }
    }

    // BB Discount - Discount Kit App
    if (settings.dkBBPdpEnabled) {
      attributes.__dkBBPdpEnabled = {
        us: settings.dkBBPdpEnabled || false,
        ca: settings.dkBBPdpEnabledCa || false
      }
    }

    // SGD
    if (settings.sgdEnabled && settings.sgdThresholds?.length && settings.sgdDiscountType) {
      attributes.__sgdDiscountType = {
        us: settings.sgdDiscountType,
        ca: null
      }
    }
    if (settings.sgdEnabledCa && settings.sgdThresholdsCa?.length && settings.sgdDiscountTypeCa && !attributes.__sgdDiscountType) {
      attributes.__sgdDiscountType = {
        us: null,
        ca: settings.sgdDiscountTypeCa
      }
    } else if (settings.sgdEnabledCa && settings.sgdThresholdsCa?.length && settings.sgdDiscountTypeCa) {
      attributes.__sgdDiscountType.ca = settings.sgdDiscountTypeCa;
    }

    if (settings.sgdEnabled && settings.sgdThresholds?.length && settings.sgdDiscounts?.length) {
      const sgdThresholdsSanitized = sanitizeArray(settings.sgdThresholds);
      if (sgdThresholdsSanitized.length) {
        attributes.__sgdThresholds = {
          us: sgdThresholdsSanitized,
          ca: null
        }
      }
    }
    if (settings.sgdEnabledCa && settings.sgdThresholdsCa?.length && settings.sgdDiscountsCa?.length && !attributes.__sgdThresholds) {
      const sgdThresholdsCaSanitized = sanitizeArray(settings.sgdThresholdsCa);
      if (sgdThresholdsCaSanitized.length) {
        attributes.__sgdThresholds = {
          us: null,
          ca: sgdThresholdsCaSanitized
        }
      }
    } else if (settings.sgdEnabledCa && settings.sgdThresholdsCa?.length && settings.sgdDiscountsCa?.length) {
      const sgdThresholdsCaSanitized = sanitizeArray(settings.sgdThresholdsCa);
      if (sgdThresholdsCaSanitized.length) attributes.__sgdThresholds.ca = sgdThresholdsCaSanitized;
    }

    if (settings.sgdEnabled && settings.sgdThresholds?.length && settings.sgdDiscounts?.length) {
      const __sgdDiscountsSanitized = sanitizeArray(settings.sgdDiscounts);
      if (__sgdDiscountsSanitized.length) {
        attributes.__sgdDiscounts = {
          us: __sgdDiscountsSanitized,
          ca: null
        }
      }
    }
    if (settings.sgdEnabledCa && settings.sgdThresholdsCa?.length && settings.sgdDiscountsCa?.length && !attributes.__sgdDiscounts) {
      const sgdDiscountsCaSanitized = sanitizeArray(settings.sgdDiscountsCa);
      if (sgdDiscountsCaSanitized.length) {
        attributes.__sgdDiscounts = {
          us: null,
          ca: sgdDiscountsCaSanitized
        }
      }
    } else if (settings.sgdEnabledCa && settings.sgdThresholdsCa?.length && settings.sgdDiscountsCa?.length) {
      const sgdDiscountsCaSanitized = sanitizeArray(settings.sgdDiscountsCa);
      if (sgdDiscountsCaSanitized.length) attributes.__sgdDiscounts.ca = sgdDiscountsCaSanitized;
    }

    if (settings.sgdEnabled && settings.sgdThresholds?.length && settings.sgdThresholdApplyDiscountCode) {
      attributes.__sgdThresholdApplyDiscountCode = {
        us: true,
        ca: false
      }
    }
    if (settings.sgdEnabledCa && settings.sgdThresholdsCa?.length && settings.sgdThresholdApplyDiscountCodeCa && !attributes.__sgdThresholdApplyDiscountCode) {
      attributes.__sgdThresholdApplyDiscountCode = {
        us: false,
        ca: true
      }
    } else if (settings.sgdEnabledCa && settings.sgdThresholdsCa?.length && settings.sgdThresholdApplyDiscountCodeCa) {
      attributes.__sgdThresholdApplyDiscountCode.ca = true;
    }

    if (settings.sgdEnabled && settings.sgdThresholds?.length && settings.sgdExcludeThresholdTagName) {
      attributes.__sgdExcludeThresholdTagName = {
        us: settings.sgdExcludeThresholdTagName,
        ca: null
      }
    }
    if (settings.sgdEnabledCa && settings.sgdThresholdsCa?.length && settings.sgdExcludeThresholdTagNameCa && !attributes.__sgdExcludeThresholdTagName) {
      attributes.__sgdExcludeThresholdTagName = {
        us: null,
        ca: settings.sgdExcludeThresholdTagNameCa
      }
    } else if (settings.sgdEnabledCa && settings.sgdThresholdsCa?.length && settings.sgdExcludeThresholdTagNameCa) {
      attributes.__sgdExcludeThresholdTagName.ca = settings.sgdExcludeThresholdTagNameCa;
    }

    if (settings.sgdEnabled && settings.sgdThresholds?.length && settings.sgdExcludeDiscountTagName) {
      attributes.__sgdExcludeDiscountTagName = {
        us: settings.sgdExcludeDiscountTagName,
        ca: null
      }
    }
    if (settings.sgdEnabledCa && settings.sgdThresholdsCa?.length && settings.sgdExcludeDiscountTagNameCa && !attributes.__sgdExcludeDiscountTagName) {
      attributes.__sgdExcludeDiscountTagName = {
        us: null,
        ca: settings.sgdExcludeDiscountTagNameCa
      }
    } else if (settings.sgdEnabledCa && settings.sgdThresholdsCa?.length && settings.sgdExcludeDiscountTagNameCa) {
      attributes.__sgdExcludeDiscountTagName.ca = settings.sgdExcludeDiscountTagNameCa;
    }

    // BD
    if (!settings.sgdEnabled && settings.bdEnabled && settings.bdDiscountType) {
      attributes.__bdDiscountType = {
        us: settings.bdDiscountType,
        ca: null
      }
    }
    if (!settings.sgdEnabledCa && settings.bdEnabledCa && settings.bdDiscountTypeCa && !attributes.__bdDiscountType) {
      attributes.__bdDiscountType = {
        us: null,
        ca: settings.bdDiscountTypeCa
      }
    } else if (!settings.sgdEnabledCa && settings.bdEnabledCa && settings.bdDiscountTypeCa) {
      attributes.__bdDiscountType.ca = settings.bdDiscountTypeCa;
    }
    if (!settings.sgdEnabled && settings.bdEnabled && settings.bdTag && settings.bdDiscounts?.length) {
      const bdDiscountsSanitized = sanitizeArray(settings.bdDiscounts);
      if (bdDiscountsSanitized.length) {
        attributes.__bdDiscounts = {
          us: bdDiscountsSanitized,
          ca: null
        }
      }
    }
    if (!settings.sgdEnabledCa && settings.bdEnabledCa && settings.bdTagCa && settings.bdDiscountsCa?.length && !attributes.__bdDiscounts) {
      const bdDiscountsCaSanitized = sanitizeArray(settings.bdDiscountsCa);
      if (bdDiscountsCaSanitized.length) {
        attributes.__bdDiscounts = {
          us: null,
          ca: bdDiscountsCaSanitized
        }
      }
    } else if (!settings.sgdEnabledCa && settings.bdEnabledCa && settings.bdTagCa && settings.bdDiscountsCa?.length) {
      const bdDiscountsCaSanitized = sanitizeArray(settings.bdDiscountsCa);
      if (bdDiscountsCaSanitized.length) attributes.__bdDiscounts.ca = bdDiscountsCaSanitized;
    }

    if (!settings.sgdEnabled && settings.bdEnabled && settings.bdFirstDiscountEnabled) {
      attributes.__bdFirstDiscountEnabled = {
        us: true,
        ca: false
      }
    }
    if (!settings.sgdEnabled && settings.bdEnabled && settings.bdFirstDiscountEnabledCa && !attributes.__bdFirstDiscountEnabled) {
      attributes.__bdFirstDiscountEnabled = {
        us: false,
        ca: true
      }
    } else if (!settings.sgdEnabled && settings.bdEnabled && settings.bdFirstDiscountEnabledCa) {
      attributes.__bdFirstDiscountEnabled.ca = true;
    }

    if (!settings.sgdEnabled && settings.bdEnabled && settings.bdTag) {
      attributes.__bdTag = {
        us: settings.bdTag,
        ca: null
      }
    }
    if (!settings.sgdEnabledCa && settings.bdEnabledCa && settings.bdTagCa && !attributes.__bdTag) {
      attributes.__bdTag = {
        us: null,
        ca: settings.bdTagCa
      }
    } else if (!settings.sgdEnabledCa && settings.bdEnabledCa && settings.bdTagCa) {
      attributes.__bdTag.ca = settings.bdTagCa;
    }

    return Object.entries(attributes).reduce((acc, curr) => {
      if (curr[1]) {
        acc[curr[0]] = JSON.stringify(curr[1]);
      } else {
        acc[curr[0]] = null;
      }
      return acc;
    } , {});
  }

  /*
    * Remove attributes with falsy values
    * @return {obj} The sanitized attributes object
  */
  const sanitizeCartAttributes = (attributes) => {
    return Object.keys(attributes).reduce((acc, key) => {
      if (attributes[key]) acc[key] = attributes[key];
      return acc;
    }, {});
  }

  /*
  * Check if the cart attributes need to be updated
  * @param {obj} settings | Cart settings
    * @return {boolean} true if the cart need to be updated, false if not
  */
  const checkCartAttributes = (cartAttributes) => {
    try {
      const localCartSettings = JSON.parse(sessionStorage.getItem('cart_settings'));
      if (!localCartSettings) return true;
      if (JSON.stringify(sanitizeCartAttributes(cartAttributes)) !== JSON.stringify(localCartSettings)) return true;
      return false;
    } catch (error) {
      console.error(error);
      return true;
    }
  }

  /*
  * Sync cart setting
  * @param {obj} settings | Cart settings
    * @return {obj}
    *   - status: 'updated' if the cart was updated, 'not updated' if not, 'fail' if something went wrong
    *   - ok: true if the request is success, false if not
    *   - cart: the updated cart
  */
  const syncCartSettings = () => new Promise(async (resolve, reject) => {
    try {
      const cartAttributes = getCartAttributes(window.cart_settings);
      if (checkCartAttributes(cartAttributes)) {
        await updateCartAttributes(cartAttributes);
        sessionStorage.setItem('cart_settings', JSON.stringify(sanitizeCartAttributes(cartAttributes)));
        resolve({ status: 'updated', ok: true, cart, error: null });
      } else {
        resolve({ status: 'not updated', ok: true, cart, error: null });
      }
    } catch (error) {
      reject({ status: 'fail', ok: false, cart, error: error });
    }
  });

  /*
    * Update cart attibutes
    * @param {obj} attributes | Cart attributes object
    * @return {obj} The new cart
  */
  const updateCartAttributes = (attributes) => new Promise(async (resolve, reject) => {
    try {
      const payload = {
        attributes
      };

      const response = await fetch(ROOT_DOMAIN + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response?.ok && response.status === 422) {
        const data = await response.json();
        throw Error('Can\'t update cart attributes', {
          cause: {
            status: response.status,
            message: data.message || data.description || response.statusText
          }
        }, 'cart-manager.js');
      } else if (!response?.ok) {
        throw 'Something went wrong.';
      }

      const data = await response.json();
      await dispatchUpdateLocalCart(data);

      resolve(data);
    } catch (error) {
      reject(error);
    }
  });

  /*
    * Add an item to cart
    * @param {obj} item | An item (id, quantity, properties)
    * @return {obj} The new item
  */
  const addItem = (item) => new Promise(async (resolve, reject) => {
    try {
      if (!item) {
        throw 'item param not specified. Function: addItem';
      };

      if (typeof item !== 'object') {
        throw 'item param must be an object. Function: addItem';
      }

      const data = await addItems([{
        id: item.id || '',
        quantity: item.quantity || 1,
        properties: item.properties,
        selling_plan: item.selling_plan || null
      }]);

      if (!data) {
        throw Error('No data returned', {cause: 'No data'}, 'cart-manager.js');
      }

      resolve(data);
    } catch (error) {
      reject(error);
    }
  });

  /*
    * Add a list of items to cart
    * @param {array} items | Array of items (id, quantity)
    * @return {obj} The new items
  */
  const addItems = (items) => new Promise(async (resolve, reject) => {
    try {
      if (!items?.length) {
        throw 'Items param not specified. Function: addItems';
      };

      if (!Array.isArray(items)) {
        throw 'Array type expected. Function: addItems';
      }

      const itemsQuantity = items.reduce((acc, curr) => acc + curr.quantity, 0);
      if ((cart?.item_count || 0) + itemsQuantity > 80) {
        throw 'Cart limit reached';
      }

      const payload = JSON.stringify({
        items: items.map(item => ({...item, properties: item.properties || {}}))
      });

      const response = await fetch(ROOT_DOMAIN + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: payload
      });

      if (!response?.ok && response.status === 422) {
        const data = await response.json();
        const description = data?.description && typeof data.description === 'string' ? data.description : null;
        if (description && (description?.includes('in your cart') || description.includes('is already sold out'))) {
          throw 'Product out of stock';
        } else if (description) {
          throw `Something went wrong: ${data.description}`;
        }
        throw 'Something went wrong';
      } else if (!response?.ok) {
        const data = await response.json();
        throw `Something went wrong: ${data.description}`;
      }

      const data = await response.json();

      await getCart();

      resolve(data);
    } catch (error) {
      reject(error);
    }
  });

  /*
    * Increase item quantity in 1
    * @param {string} itemKey | A variant id
    * @param {number} sellingPlanId | Item's selling plan id to identify if subscription | 'null' if no subscription
    * @return {obj} The updated item
  */
  const increaseItemQuantity = (itemKey, sellingPlanId) => new Promise(async (resolve, reject) => {
    try {
      if (!itemKey) {
        throw 'itemKey param not specified. Function: increaseItemQuantity';
      }

      if (cart?.item_count > 79) {
        throw 'Cart limit reached';
      }

      const payload = {
        key: itemKey,
        quantity: cart?.items?.find(item => item.key == itemKey)?.quantity + 1 || 1
      };

      const data = await updateItemQuantity(payload);

      if (!data) {
        throw Error('No data returned', {cause: 'No data'}, 'cart-manager.js');
      }

      const updatedItem = data.items.find(item => {
        const updatedSellingPlanId = item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : null;
        return item.id == itemKey.split(':')[0] && !item.properties._bundleId && !item.properties._isGWP && updatedSellingPlanId === sellingPlanId
      });

      resolve(updatedItem);
    } catch (error) {
      reject(error);
    }
  });

  /*
    * Increase bundle quantity in 1
    * @param {string} bundleId | A unique bundle id
    * @return {obj} The updated item
  */
  const increaseBundleQuantity = (bundleId) => new Promise(async (resolve, reject) => {
    try {
      if (!bundleId) {
        throw 'bundleId param not specified. Function: increaseBundleQuantity';
      }

      const bundle = cart?.items?.find(item => item.properties?._bundleId == bundleId);

      if (!bundle) {
        throw 'A bundle with the specified ID does not exist. Function: increaseBundleQuantity';
      }

      const bundleItemsQuantity = bundle.properties?._bundleId.split('/').length;
      const bundleQuantity = bundle.quantity + 1;
      if ((cart?.item_count || 0) + ((bundleItemsQuantity || 2) * bundleQuantity) > 80) {
        throw 'Cart limit reached';
      }

      const payload = {
        id: bundleId,
        quantity: bundleQuantity
      };

      const data = await updateBundleQuantity(payload);

      if (!data) {
        throw Error('No data returned', {cause: 'No data'}, 'cart-manager.js');
      }

      const updatedItem = data.items.find(item => item.properties?._bundleId == bundleId);

      resolve(updatedItem);
    } catch (error) {
      reject(error);
    }
  });

  /*
    * Decrease item quantity in 1
    * @param {string} itemKey | A variant id
    * @param {number} sellingPlanId | Item's selling plan id to identify if subscription | 'null' if no subscription
    * @return {obj} The updated item
  */
  const decreaseItemQuantity = (itemKey, sellingPlanId) => new Promise(async (resolve, reject) => {
    try {
      if (!itemKey) {
        throw 'itemKey param not specified. Function: decreaseItemQuantity';
      }

      const payload = {
        key: itemKey,
        quantity: cart?.items?.find(item => item.key == itemKey)?.quantity - 1 || 0
      }

      const data = await updateItemQuantity(payload);

      if (!data) {
        throw Error('No data returned', {cause: 'No data'}, 'cart-manager.js');
      }

      if (!data.items.length) {
        resolve({key: itemKey, quantity: 0});
        return;
      }

      const updatedItem = data.items.find(item => {
        const updatedSellingPlanId = item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : null;
        return item.id == itemKey.split(':')[0] && !item.properties._bundleId && !item.properties._isGWP && updatedSellingPlanId == sellingPlanId;
      });

      if (!updatedItem) {
        resolve({key: itemKey, quantity: 0});
        return;
      }

      resolve(updatedItem);
    } catch (error) {
      reject(error);
    }
  });

  /*
    * Decrease item quantity in 1
    * @param {string} itemKey | A variant id
    * @return {obj} The updated item
  */
  const decreaseBundleQuantity = (bundleId) => new Promise(async (resolve, reject) => {
    try {
      if (!bundleId) {
        throw 'bundleId param not specified. Function: decreaseBundleQuantity';
      }

      const payload = {
        id: bundleId,
        quantity: cart?.items?.find(item => item.properties?._bundleId == bundleId)?.quantity - 1 || 0
      }

      const data = await updateBundleQuantity(payload);

      if (!data) {
        throw Error('No data returned', {cause: 'No data'}, 'cart-manager.js');
      }

      if (!data.items.length) {
        resolve({id: bundleId, quantity: 0});
        return;
      }

      const updatedItem = data.items.find(item => item.properties?._bundleId == bundleId);

      if (!updatedItem) {
        resolve({id: bundleId, quantity: 0});
        return;
      }

      resolve(updatedItem);
    } catch (error) {
      reject(error);
    }
  });

  /*
    * Apply discount code to the cart
    * @param {string} Discount code
    * @return {obj} status, ok, message, code
  */
  const applyDiscountCode = (discountCode) => new Promise(async (resolve, reject) => {
    try {
      if (!discountCode?.length) {
        throw 'DISCOUNT_CODE_MISSING';
      } else if (discountCode.length < 3) {
        throw 'DISCOUNT_CODE_INVALID'
      }

      const discountCodeUpperCase = discountCode.toUpperCase();

      // Get cart token
      const payConfigResponse = await fetch('/payments/config', { method: 'GET' });
      if (!payConfigResponse?.ok) {
        throw 'DISCOUNT_CODE_UNEXPECTED_ERROR';
      }
      const payConfigJSon = await payConfigResponse.json();
      const accessToken = btoa(payConfigJSon.paymentInstruments.accessToken);
      if (!accessToken) {
        throw 'DISCOUNT_CODE_UNEXPECTED_ERROR';
      }

      // Create a checkout to validate the discount code
      const checkoutResponse = await fetch('/wallets/checkouts/', {
        'headers': {
          'accept': '*/*', 'cache-control': 'no-cache',
          'authorization': 'Basic ' + accessToken,
          'content-type': 'application/json, text/javascript',
          'pragma': 'no-cache', 'sec-fetch-dest': 'empty',
          'sec-fetch-mode': 'cors', 'sec-fetch-site': 'same-origin'
        },
        'referrerPolicy': 'strict-origin-when-cross-origin',
        'method': 'POST', 'mode': 'cors', 'credentials': 'include',
        'body': JSON.stringify({
          'checkout': {
            'country': Shopify.country,
            'discount_code': discountCodeUpperCase,
            'line_items': cart.items,
            'presentment_currency': Shopify.currency.active
          }
        })
      });

      // Check if the discount code exists
      if (!checkoutResponse?.ok) {
        throw 'DISCOUNT_CODE_NOT_EXIST';
      }

      // Check if the discount code is applicable
      const checkoutJson = await checkoutResponse.json();
      if (!checkoutJson?.checkout?.applied_discounts?.length) throw 'DISCOUNT_CODE_NOT_APPLICABLE';

      // Apply discount code to cart
      const discountCodes = [discountCodeUpperCase, ...cart.discount_codes.map(discount => discount.title.toUpperCase())].join(',');
      try { await fetch(`/checkout?discount=${discountCodes}`, { redirect: 'manual' }); } catch (e) {}

      // Check if the discount code was applied
      const newCart = await getCart();
      if (!newCart?.discount_codes?.map(discount => discount.title.toUpperCase()).includes(discountCodeUpperCase)) throw 'DISCOUNT_CODE_NOT_APPLICABLE_WITH_EXISTING_DISCOUNT';

      // Add the new discount codes to the cart attributes
      await updateCartAttributes({
        'discount_codes': discountCodes
      });

      resolve({ status: 'ok', ok: true, message: 'Discount code applied successfully', code: 'DISCOUNT_CODE_APPLIED' });
    } catch(error) {
      reject({ status: 'fail', ok: false, message: error.statusText || error.message || 'Discount code not applied', code: error.status || error });
    }
  });

  /*
    * Update item quantity in cart
    * @param {obj} item | An item (id, quantity). If quantity is 0, null or undefined the item is removed
    * @return {obj} The new cart
  */
  const updateItemQuantity = (item, position, properties = null) => new Promise(async (resolve, reject) => {
    try {
      const payload = {
        quantity: item.quantity || 0
      };

      if (position) {
        payload.line = position;
      } else {
        payload.id = item.key;
      }

      if (properties) {
        payload.properties = properties;
      }

      const response = await fetch(ROOT_DOMAIN + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response?.ok && response.status === 422) {
        const data = await response.json();
        throw Error('Can\'t update item quantity', {
          cause: {
            status: response.status,
            message: data.message || data.description || response.statusText
          }
        }, 'cart-manager.js');
      } else if (!response?.ok) {
        throw 'Something went wrong.';
      }

      const data = await response.json();
      
      await dispatchUpdateLocalCart(data);

      resolve(data);
    } catch (error) {
      reject(error);
    }
  });

  /*
    * Update multiple item quantities
    * @param {array} quantities | An array of quantities. If quantity is 0, null or undefined the item is removed
    * @return {obj} The new cart
  */
  const updateMultipleItemsQuantity = (quantities) => new Promise(async (resolve, reject) => {
    if (!quantities.length) {
      throw Error('quantities param is required', null, 'cart-manager.js');
    }

    const payload = {
      updates: quantities
    };

    const response = await fetch(ROOT_DOMAIN + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response?.ok && response.status === 422) {
      const data = await response.json();
      throw Error('Can\'t update item quantity', {
        cause: {
          status: response.status,
          message: data.message || data.description || response.statusText
        }
      }, 'cart-manager.js');
    } else if (!response?.ok) {
      throw 'Something went wrong.';
    }

    const data = await response.json();

    await dispatchUpdateLocalCart(data);

    resolve(data);
  });

  /*
    * Update bundle quantity in cart
    * @param {obj} item | An item (id, quantity). If quantity is 0, null or undefined the item is removed
    * @return {obj} The new cart
  */
  const updateBundleQuantity = (bundle) => new Promise(async (resolve, reject) => {
    try {
      if (!bundle?.id) {
        throw Error('bundle param is required', null, 'cart-manager.js');
      }

      const itemsToUpdate = cart.items.map(item => {
        if (item.properties._bundleId === bundle.id) {
          return bundle.quantity;
        } else {
          return item.quantity;
        }
      });

      const data = await updateMultipleItemsQuantity(itemsToUpdate);

      if (!data) {
        throw Error('No data returned', {cause: 'No data'}, 'cart-manager.js');
        return;
      }

      resolve(data);
    } catch (error) {
      reject(error);
    }
  });

  /*
    * Remove an item from cart
    * @param {string} itemKey | A variant id
    * @return {obj} The updated cart
  */
  const removeItem = (itemKey) => new Promise(async (resolve, reject) => {
    try {
      if (!itemKey) {
        throw 'itemKey param is not specified. Function: removeItem';
      }

      const data = await updateItemQuantity({key: itemKey, quantity: 0});

      if (!data) {
        throw Error('No data returned', {cause: 'No data'}, 'cart-manager.js');
      }

      resolve(data);
    } catch (error) {
      reject(error);
    }
  });

  /*
    * Create a bundle with items in cart
    * @param {array} items | An array of items
    * @return {obj} The new item
  */
  const createBundle = (items) => new Promise(async (resolve, reject) => {
    try {
      if (!items?.length) {
        throw 'Items param not specified. Function: createBundle';
      }

      if (!Array.isArray(items)) {
        throw 'Array type expected. Function: createBundle';
      }

      const cart = await getCart();
      const tag = items[0].tag;
      const itemIds = items.map(item => item.id);

      const bundleIds = permutator(itemIds).map(item => {
        return item.reduce((acc, curr) => {
          return acc ? `${acc}/${curr}` : curr;
        }, '');
      });

      const bundles = cart.items.filter(cartItem => {
        return cartItem.properties._abId && cartItem.properties._tags?.includes(tag);
      }).reduce((bundle, item) => {
        if (bundle[item.properties._bundleId]) {
          if (bundle[item.properties._bundleId][item.id]) {
            bundle[item.properties._bundleId][item.id].quantity += item.quantity;
          } else {
            bundle[item.properties._bundleId][item.id] = {
              id: item.id,
              bundleId: item.properties._bundleId,
              quantity: item.quantity
            }
          }
          return bundle;
        } else {
          bundle[item.properties._bundleId] = {};
          bundle[item.properties._bundleId][item.id] = {
            id: item.id,
            bundleId: item.properties._bundleId,
            quantity: item.quantity
          }
          return bundle;
        }
      }, {});

      const bundleId = bundleIds.find(bundleId => bundles[bundleId]);

      const payload = [];
      cart.items.forEach(cartItem => {
        const item = items.find(item => item.id === cartItem.id && !cartItem.properties._bundleId && !cartItem.properties._bbId && !item.selling_plan_allocation?.selling_plan?.id);
        if (item) {
          payload.push(cartItem.quantity - item.quantity);
        } else if (bundleId && cartItem.properties._bundleId === bundleId) {
          payload.push(cartItem.quantity + 1);
        } else {
          payload.push(cartItem.quantity);
        }
      });

      await updateMultipleItemsQuantity(payload);
      await getCart();

      if (!bundleId) {
        const bundleItemsToCreate = [];
        let index = 0;
        const itemsCount = items.reduce((acc, item) => acc + item.quantity, 0);
        items.forEach(item => {
          for (let i = 0; i < item.quantity; i++) {
            const newItem = JSON.parse(JSON.stringify(item));
            newItem.quantity = 1;
            newItem.properties._index = index;
            if (index === itemsCount - 1) {
              newItem.properties._first = true;
            } else {
              delete newItem.properties._first;
            }
            if (index === 0) {
              newItem.properties._last = true;
            } else {
              delete newItem.properties._last;
            }
            bundleItemsToCreate.push(newItem);
            index++;
          }
        });
        const data = await addItems(bundleItemsToCreate.sort((a, b) => {
          if (a.properties._index < b.properties._index) return 1;
          if (a.properties._index > b.properties._index) return -1;
          return 0;
        }));
        resolve(data.items);
      } else {
        const newCart = await getCart();
        resolve(newCart.items.find(item => item.properties._bundleId === bundleId));
      }
    } catch (error) {
      reject(error);
    }
  });

  /*
    * Remove a bundle from cart
    * @param {string} bundleId | A unique bundle id
    * @return {obj} The updated cart
  */
  const removeBundle = (bundleId) => new Promise(async (resolve, reject) => {
    try {
      if (!bundleId) {
        throw 'bundleId param not specified. Function: removeItem';
      }

      const data = await updateBundleQuantity({id: bundleId, quantity: 0});

      if (!data) {
        throw Error('No data returned', {cause: 'No data'}, 'cart-manager.js');
      }

      resolve(data);
    } catch (error) {
      reject(error);
    }
  });

  const checkIfCartHasProductsWithDiscountKit = () => {

    // Check if is CA
    let isCa = window.Shopify.country === 'CA';
    let dkBBDiscountTag = isCa ? window.cart_settings.dkBBDiscountTagCa : window.cart_settings.dkBBDiscountTag;

    return cart.items.some(item => item.properties._tags?.some(tag => tag.includes(dkBBDiscountTag)));
  }

  /*
    * DISABLED (TLDEV-589). Kept for reference, do not re-enable as is.
    *
    * This persisted _hasDiscountKitApplied on every Discount Kit line item, on every page
    * load. Two problems:
    *
    * 1. Discount Kit regroups cart lines to allocate its tiers ("3 for $49"), and that
    *    changes the line item keys. By the time this wrote, the key it held was gone, so
    *    Shopify answered 400 {"message":"no valid id or line parameter"} every time. Since
    *    the value never persisted, it retried on every page load and every cart update,
    *    forever.
    * 2. Each attempt dispatched a full render-cart-items per line item, and every render
    *    fires a batch of product requests. That exhausted Shopify's per session Ajax cart
    *    quota, and from then on every cart call got a 429: customers could not remove
    *    items, add the bundle, or reach the checkout.
    *
    * The flag is derived state. Read it with checkIfProductHasDiscountKitApplied, which
    * computes it from the line item discounts, instead of storing it on the cart. If this
    * ever needs to write again, address the line by position (updateItemQuantity accepts a
    * position and sends `line`) and re-read the cart first, because the key is not stable.
  */
  // const updateDiscountKitProperties = () => new Promise(async (resolve, reject) => {
  // try {
  // if (!cart.items.length) {
  // return resolve(false);
  // }
  //
  // // Check if is CA
  // let isCa = window.Shopify.country === 'CA';
  // let dkBBDiscountTag = isCa ? window.cart_settings.dkBBDiscountTagCa : window.cart_settings.dkBBDiscountTag;
  //
  // // Check each line item if it has the discount kit tag and if it has the discount kit applied or not
  // cart.items.forEach(async item => {
  // if (item.properties._tags?.some(tag => tag.includes(dkBBDiscountTag))) {
  // const hasDiscountKit = checkIfProductHasDiscountKitApplied(item);
  // const properties = {
  // ...item.properties,
  // _hasDiscountKitApplied: hasDiscountKit,
  // };
  //
  // await updateItemQuantity(item, null, properties);
  // window.broadcastChannel.dispatchEvent('render-cart-items', { openCartDrawer: false });
  // }
  // });
  //
  // resolve(true);
  // } catch (error) {
  // reject(error);
  // }
  // });

  const itemHasDiscountKitPercentTag = (item) => {
    const isCa = window.Shopify.country === 'CA';
    const dkBBDiscountTag = isCa ? window.cart_settings.dkBBDiscountTagCa : window.cart_settings.dkBBDiscountTag;
    let tags = item.properties._tags || [];
    if (!isCa) {
      tags = tags.filter(tag => !tag.includes(window.cart_settings.dkBBDiscountTagCa));
    }
    const discountTag = tags.find(tag => tag.includes(dkBBDiscountTag));
    return !!(discountTag && discountTag.includes('%'));
  };

  const checkIfProductHasDiscountKitApplied = (item) => {
    if (!item.discounts?.length) return false;
    const discountTitle = item.discounts[0]?.title;
    if (!discountTitle) return false;
    if (itemHasDiscountKitPercentTag(item)) {
      return discountTitle.includes('%');
    }
    return discountTitle.includes('for $');
  }

  /*
    * Check if AB Bundles can be created
    * @param {string} tag | An optional tag (if tag is specified, check if a AB Bundle can be created with that tag)
    * @return {obj} | true or false
  */
  const checkAutoBundle = (tag) => {
    if (!cart.items.length) {
      return false;
    }

    const settings = filterSettingsByCountry(window.cart_settings);
    if (!settings.abRepeatProductVariantEnabled && cart.items.length <= 1) {
      return false;
    }

    const abBundlesCount = cart.items
      .filter(item => item.properties._abId && item.properties._first)
      .reduce((acc, item) => acc + item.quantity, 0);

    let globalLimit = settings.abLimit || 100;
    if (globalLimit > 100 || globalLimit < 1) {
      globalLimit = 100;
    }
    if (abBundlesCount >= globalLimit) {
      return false;
    }

    if (tag && (typeof tag !== 'string' || !tag.includes('auto-bundle-') || !validateABTag(tag))) {
      return false;
    }

    if (tag) {
      let itemsAmount;
      if (settings.abRepeatProductVariantEnabled) {
        itemsAmount = cart.items.reduce((acc, item) => {
          if (!item.properties._abId && item.properties._tags?.some(_tag => _tag === tag)) {
            return acc + item.quantity;
          }
          return acc;
        }, 0);
      } else {
        itemsAmount = cart.items.filter(item => !item.properties._abId && item.properties._tags?.some(_tag => _tag === tag)).length;
      }
      const abQuantity = +tag.split('-')[3];
      const abLimitByTag = +tag.split('-')[5] || 100;
      const abBundleCountByTag = cart.items.reduce((acc, item) => {
        if (item.properties._abId && item.properties._tags?.includes(tag) && item.properties._first) return acc + item.quantity;
        return acc;
      }, 0);
      if (itemsAmount >= abQuantity && abBundleCountByTag < abLimitByTag) {
        return true;
      } else {
        return false;
      }
    }

    const existABTags = cart.items.some((item, index, arr) => {
      if (item.properties._bbId || item.selling_plan_allocation?.selling_plan?.id) return false;
      const abTag = item.properties._tags?.find(tag => tag.includes('auto-bundle-') && validateABTag(tag));
      if (!abTag) return false;
      const abQuantity = +abTag.split('-')[3];
      if (!abQuantity) return false;

      const abLimitByTag = +abTag.split('-')[5] || 100;
      const abBundleCountByTag = cart.items.reduce((acc, item) => {
        if (item.properties._abId && item.properties._tags?.includes(abTag) && item.properties._first) return acc + item.quantity;
        return acc;
      }, 0);

      let itemsAmount;
      if (settings.abRepeatProductVariantEnabled) {
        itemsAmount = arr.reduce((acc, item) => {
          if (!item.properties._abId && item.properties._tags?.some(tag => tag === abTag)) {
            return acc + item.quantity;
          }
          return acc;
        }, 0);
      } else {
        itemsAmount = arr.filter(item => !item.properties._abId && item.properties._tags?.some(tag => tag === abTag)).length;
      }
      if (itemsAmount >= abQuantity && abBundleCountByTag < abLimitByTag) return true
      return false;
    });

    return existABTags;
  };

  /*
    * Create auto bundles (AB)
    * @return {obj} | The new cart, the new items, status and errors
  */
  const autoBundling = () => new Promise(async (resolve, reject) => {
    try {
      if (!cart.items.length) {
        resolve({ status: 'not updated', ok: true, cart, newItems: [], error: null });
        return;
      }

      const settings = filterSettingsByCountry(window.cart_settings);

      const abTags = [];
      const bundleItems = [];

      const abBundlesCount = cart.items
        .filter(item => item.properties._abId && item.properties._first)
        .reduce((acc, item) => acc + item.quantity, 0);

      // Stop if the Global AB Limit is reached
      let globalLimit = settings.abLimit || 100;
      if (globalLimit > 100 || globalLimit < 1) {
        globalLimit = 100;
      }
      if (abBundlesCount >= globalLimit) {
        resolve({ status: 'not updated', ok: true, cart, newItems: [], error: null });
        return;
      }

      cart.items.forEach(item => {
        if (item.properties._tags?.length) {
          item.properties._tags?.forEach(tag => {
            if (tag.includes('auto-bundle-') && validateABTag(tag) && !abTags.includes(tag)) abTags.push(tag);
          });
        }
      });

      if (!abTags.length) {
        resolve({ status: 'not updated', ok: true, cart, newItems: [], error: null });
        return;
      }

      const abTagsFiltered = abTags.filter(tag => checkAutoBundle(tag));
      const abTag = abTagsFiltered[0];
      const abQuantity = +abTag.split('-')[3];
      let abLimitByTag = +abTag.split('-')[5];

      if (!abLimitByTag || abLimitByTag > 100 || abLimitByTag < 1) {
        abLimitByTag = 100;
      }

      const abBundleCountByTag = cart.items.reduce((acc, item) => {
        if (item.properties._abId && item.properties._tags?.includes(abTag) && item.properties._first) return acc + item.quantity;
        return acc;
      }, 0);

      // Stop if the AB Tag Limit is reached
      if (abLimitByTag && abBundleCountByTag && abBundleCountByTag >= abLimitByTag) {
        return;
      }

      let abItems = cart.items.filter(item => item.properties._tags?.some(tag => tag === abTag) && !item.properties._bundleId && !item.properties._bbId && !item.selling_plan_allocation?.selling_plan?.id && !item.properties._isGWP);

      if (!settings.abRepeatProductTypeEnabled) {
        abItems = abItems.filter((value, index, self) =>
          index === self.findIndex((t) => (
            t.product_type === value.product_type
          ))
        )
      }

      if (!settings.abRepeatProductColorEnabled) {
        abItems = abItems.filter((value, index, self) =>
          index === self.findIndex((item) => (
            item.product_id === value.product_id
          ))
        )
      }

      if (!abItems.length) return;

      if (!settings.abRepeatProductVariantEnabled) {
        abItems = abItems.map(item => ({...item, quantity: 1}));
      }

      const abItemsQuantity = abItems.reduce((acc, item) => acc + item.quantity, 0);

      if (abItemsQuantity > abQuantity) {
        let acc = 0;
        abItems = abItems.map((item) => {
          if (acc === abQuantity) return {item, quantity: 0};
          if ((acc + item.quantity) === abQuantity) {
            acc += item.quantity;
            return item;
          }
          if ((acc + item.quantity) > abQuantity) {
            const newQuantity = abQuantity - acc;
            acc += newQuantity;
            return { ...item, quantity: newQuantity };
          }
          acc += item.quantity;
          return item;
        }).filter(item => !!item.quantity);
      }

      if (abItemsQuantity >= abQuantity) {
        const abId = abTag.split('-')[2];
        const comparePrice = abItems.reduce((acc, item) => acc += item.price * item.quantity, 0);
        let abPrice = abTag.split('-')[4];
        if (abPrice.slice(-1) === '%') {
          abPrice = Math.round((comparePrice - comparePrice * abPrice.slice(0, -1) / 100) / 100) * 100;
        } else {
          abPrice = abPrice * 100;
        }
        const bundleId = abItems.reduce((acc, item) => {
          for (let i = 0; i < item.quantity; i++) {
            if (acc) {
              acc += `/${item.id}`;
            } else {
              acc = item.id;
            }
          }
          return acc;
        }, '');
        abItems.forEach((item, index, arr) => {
          const properties = item.properties;
          properties._abId = abId;
          properties._abTag = abTag;
          properties._bundleUniqId = bundleId;
          properties._bundleId = bundleId;
          properties._bundlePrice = abPrice;
          properties._bundlePriceCa = Math.round(abPrice * window.i18n_cad_exchange_rate);
          properties._compareAtPrice = comparePrice;
          properties._compareAtPriceCa = Math.round(abPrice * window.i18n_cad_exchange_rate);
          properties._bundleProduct = item.handle;
          delete properties['Final Sale'];
          bundleItems.push({
            id: item.id,
            key: item.key,
            quantity: item.quantity,
            properties: properties,
            tag: abTag
          });
        });
      }

      if (bundleItems.length) {
        await createBundle(bundleItems);
        if (checkAutoBundle()) {
          const response = await autoBundling();
          resolve({ status: response.status, ok: response.ok, cart, error: null });
        } else {
          resolve({ status: 'updated', ok: true, cart, error: null });
        }
      } else {
        resolve({ status: 'updated', ok: true, cart, error: null });
      }
    } catch (error) {
      console.error(error);
      reject({ status: 'fail', ok: false, cart, error });
    }
  });

  /*
    * Permute an array
    * @param {array} An array
    * @return {array} An array of arrays (permutations)
  */
  const permutator = (inputArr) => {
    let result = [];

    const permute = (arr, m = []) => {
      if (arr.length === 0) {
        result.push(m)
      } else {
        for (let i = 0; i < arr.length; i++) {
          let curr = arr.slice();
          let next = curr.splice(i, 1);
          permute(curr.slice(), m.concat(next))
       }
     }
   }

   permute(inputArr)

   return result;
  }

  /*
    * Validate an ABTag (Auto Bundle Tag)
    * @param {string} ABTag
    * @return {Boolean} True if the ABTag is well formed
  */
  const validateABTag = (tag) => {
    if (!tag || typeof tag !== 'string') return false;
    if (tag.length < 19) return false;
    if (tag.length > 41) return false;
    const tagParts = tag.split('-');
    if (tagParts.length < 5) return false;
    if (tagParts.length > 6) return false;
    if (tagParts[0] !== 'auto') return false;
    if (tagParts[1] !== 'bundle') return false;
    const id = tagParts[2];
    if (!id || !id.length || id.length > 16) return false;
    const quantity = +tagParts[3];
    if (!quantity || typeof quantity !== 'number' || quantity < 2 || quantity > 20) return false;
    const discount = tagParts[4];
    if (!discount || !discount.length || discount.length < 2 || discount.length > 4) return false;
    if (discount.slice(-1) === '%' && (+discount.slice(0, -1) < 1 || +discount.slice(0, -1) > 100)) return false;
    if (discount.slice(-1) !== '%' && (+discount < 1 || +discount > 1000)) return false;
    const limit = tagParts[5];
    if (limit && (typeof +limit !== 'number' || +limit < 1 || +limit > 100)) return false;
    return true;
  }

  (async () => {
    // The cartManager object and its methods is globally available on the window object.
    window.cartManager = Object.freeze({
      clearCart,
      getLocalCart,
      getLocalCartAsync,
      updateLocalCart,
      sanitize,
      syncCartSettings,
      addItem,
      addItems,
      increaseItemQuantity,
      increaseBundleQuantity,
      decreaseItemQuantity,
      decreaseBundleQuantity,
      applyDiscountCode,
      removeItem,
      removeBundle,
      checkAutoBundle,
      autoBundling,
      // updateDiscountKitProperties, // DISABLED (TLDEV-589) - see the function above
      checkIfProductHasDiscountKitApplied,
      checkIfCartHasProductsWithDiscountKit
    });

    // Fetch the cart data from api and set the local cart object
    await getCart();
  })();
})();
